import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';

import ForecastDate from '../layerselector/ForecastDate';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackward, faExclamationTriangle, faForward, faInfoCircle, faRotateRight } from '@fortawesome/free-solid-svg-icons';

import { MapActiveLayer, TimelineStateI } from '../../Map.types';
import ModalWidget from '../widgets/ModalWidget';
import { DurationConstructor, expandDenominator, getForecastStepUnit, groupFullName } from '../layerselector/LayerSelector.utils';
import TimelineSlider from './TimelineSlider';
import AdditionalSteps from './AdditionalSteps';
import { deriveCadenceTicks } from './TimelineSlider.utils';
import type { AvailabilityApiResponse } from './Timeline.types';

import './Timeline.css';
import { Tooltip } from '@mui/material';

export default memo(function Timeline(props: {
    state?: TimelineStateI,
    currentForecastDate: string,
    activeLayers: MapActiveLayer[],
    onUpdateDimension: (layer: MapActiveLayer, dimension: string, value: string, forecastDate?: string) => void
    onSetForecastDate: (date: string) => void,
    date?: string,
    time?: string,
    lastForecastDate: string,
    ignoreLatestForecastTime?: boolean
}) {

    const { currentForecastDate, activeLayers, onUpdateDimension } = props;

    const [currentStep, setCurrentStep] = useState<number>(0);

    const [timelineAvailability, setTimelineAvailability] = useState<AvailabilityApiResponse>({ available_steps: [0, 1, 2, 3, 4, 5, 6], min: 0, max: 7 });
    const [isPreloading, setIsPreloading] = useState<boolean>(false);

    const [selectedDate, setSelectedDate] = React.useState<string>(moment.utc(props.currentForecastDate).format("YYYY-MM-DDTHH:mm") + "Z");
    const [layerAvailabilities, setLayerAvailabilities] = useState<{ [layer: string]: AvailabilityApiResponse }>({});
    // const interval = props.state?.interval.charAt(0);
    const animationTimeUnit: DurationConstructor = expandDenominator(props.state?.interval.charAt(1) || DurationConstructor.hours);
    const forecastStepUnit: DurationConstructor = getForecastStepUnit(props.state?.interval.charAt(1) || DurationConstructor.hours);
    const [state, setState] = useState<TimelineStateI | undefined>(props.state);

    // The additional steps continue the slider's index space rather than living in one of their own:
    // indices min..max are slider cells, max+1..maxIndex are the aggregate-window buttons. That way
    // forward/back, the step readout and the commit path need no special cases.
    const stepIndex = useMemo(() => {
        const additional = timelineAvailability.additional_steps || [];
        const firstAdditionalIndex = timelineAvailability.max + 1;
        return {
            additional,
            /** Highest selectable index, buttons included. */
            maxIndex: timelineAvailability.max + additional.length,
            /** Total number of selectable forward positions, for the "n/total" readout. */
            total: firstAdditionalIndex + additional.length,
            isAdditional: (index: number) => index >= firstAdditionalIndex,
            additionalAt: (index: number) => additional[index - firstAdditionalIndex],
            /** Offset from the base forecast date, in animationTimeUnit, for a non-negative index. */
            offsetAt: (index: number) => index >= firstAdditionalIndex
                ? additional[index - firstAdditionalIndex]?.step
                : timelineAvailability.available_steps[index],
            positionOf: (index: number) => index - firstAdditionalIndex,
        };
    }, [timelineAvailability]);

    const activeAdditional = stepIndex.additionalAt(currentStep);
    const activeAdditionalIndex = activeAdditional ? stepIndex.positionOf(currentStep) : null;

    useEffect(() => {
        const areSameLayers = (left?: MapActiveLayer[], right?: MapActiveLayer[]) => {
            if (left === right) return true;
            if (!left || !right) return false;
            if (left.length !== right.length) return false;
            const leftNames = left.map((layer) => layer.name).sort();
            const rightNames = right.map((layer) => layer.name).sort();
            return leftNames.every((name, idx) => name === rightNames[idx]);
        };

        if (!areSameLayers(props.state?.layers, state?.layers) ||
            (!props.state?.layers || !state?.layers)
        ) {
            setState(props.state);
        }
    }, [props.state, state?.layers]);

    const onSetTimelineAvailability = (data: AvailabilityApiResponse) => {
        setCurrentStep(data.min);
        setTimelineAvailability(data);
    };

    useEffect(() => {
        if (!props.currentForecastDate || !state || !state.layers) return;
        setSelectedDate(moment.utc(props.currentForecastDate).format("YYYY-MM-DDTHH:mm") + "Z");
        setLayerAvailabilities({});

        for (const layer of state.layers) {
            fetch(`/api/fms/availability/${props.currentForecastDate}/${layer.name}/`)
                .then(res => {
                    if (!res.ok) {
                        throw new Error("Network response was not ok");
                    }
                    return res.json()
                })
                .then((data: AvailabilityApiResponse) => {
                    if (data.message) {
                        console.error("Error fetching available steps:", data.message);
                        throw new Error(data.message);
                    } else {
                        setLayerAvailabilities((prev) => ({ ...prev, [layer.name]: data }));
                    }
                }).catch((error) => {
                    console.error("Error fetching available steps:", error);
                    onSetTimelineAvailability({ available_steps: [0, 1, 2, 3, 4, 5, 6], min: 0, max: 7 });
                });
        }
    }, [props.currentForecastDate, state]);
    useEffect(() => {
        // set to the longest available_steps
        setTimelineAvailability(Object.values(layerAvailabilities).reduce((a, b) => a.available_steps.length > b.available_steps.length ? a : b, { available_steps: [], min: 0, max: 0 }));
    }, [layerAvailabilities]);

    useEffect(() => {
        // reset to step 0 when availability changes
        handleUpdateAnimationStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleUpdateAnimationStep is declared below this effect (temporal dead zone) and its identity changes with currentForecastDate/activeLayers; adding it would reset the step on unrelated changes
    }, [timelineAvailability]);

    const hasDifferentSteps = useMemo(() => {
        const lengths = Object.values(layerAvailabilities).map(l => l.available_steps.length);
        return new Set(lengths).size > 1;
    }, [layerAvailabilities]);

    const handleUpdateAnimationStep = useCallback((val: number) => {
        let newForecastDate;
        if (val >= 0) {
            // Covers the additional steps too - their `step` is the offset of the window's first
            // timestep, so the date the layers get is computed exactly like a normal step's.
            const offset = stepIndex.offsetAt(val);
            // A malformed payload would otherwise reach moment().add(undefined), which silently
            // yields the base date and looks like a working step-0 commit.
            if (offset === undefined) {
                // An availability with no steps at all is a normal state - the layer is simply not
                // available for this run, and the widget renders its unavailable branch. Anything
                // else here is a malformed payload.
                if (timelineAvailability.available_steps.length || stepIndex.additional.length) {
                    console.warn("No step offset for timeline index", val);
                }
                // Still move the selection - the availability reset calls this with 0 even when the
                // layer has no steps at all, and the index must not be left pointing at the old range.
                setCurrentStep(val);
                return;
            }
            newForecastDate = moment.utc(currentForecastDate).add(offset, animationTimeUnit).format("YYYY-MM-DDTHH:mm") + "Z";
        } else {
            // different time steps get to navigate backwards differently
            let backwardInterval = DurationConstructor.hours;
            if (animationTimeUnit === 'months') backwardInterval = DurationConstructor.months;
            else if (animationTimeUnit === 'days') backwardInterval = DurationConstructor.days;
            else if (animationTimeUnit === 'weeks') {
                backwardInterval = DurationConstructor.hours;
                val *= 12;
            }

            newForecastDate = moment.utc(currentForecastDate).add(val, backwardInterval).format("YYYY-MM-DDTHH:mm") + "Z";
        }
        // add 1 but it could be -1 or +1
        setCurrentStep(val);
        setSelectedDate(newForecastDate)
        activeLayers.forEach((layer) => {
            if (layer.layerInfo && layer.layerInfo.Keywords) {
                onUpdateDimension(layer, layer.layerInfo.Keywords['animation_dimension'] || "reftime", newForecastDate, currentForecastDate);
            }
        });
    }, [currentForecastDate, activeLayers, onUpdateDimension, timelineAvailability, stepIndex, animationTimeUnit]);

    const handlePreload = async () => {
        if (isPreloading) return;
        setIsPreloading(true);
        let step = currentStep;
        for (let i = 0; i < 10; i++) {
            if (step >= timelineAvailability.max) break;
            await new Promise(r => setTimeout(r, 300));
            step += 1;
            handleUpdateAnimationStep(step);
        }
        await new Promise(r => setTimeout(r, 300));
        handleUpdateAnimationStep(0);
        setIsPreloading(false);
    }

    const formatDisplayDate = useCallback((date: moment.MomentInput) => {
        const momentDate = moment.utc(date);
        return animationTimeUnit === 'weeks' ? momentDate.format('YYYY-MM-DD') :
            animationTimeUnit === 'months' ? momentDate.format('YYYY-MM') :
                momentDate.format("YYYY-MM-DD HH:mm") + "Z";
    }, [animationTimeUnit]);

    // available_steps is sparse and its cadence changes - typically hourly for the first few steps
    // and then 6-hourly, e.g. [0,1,2,3,4,5,6,12,18,...]. Mark those boundaries on the slider.
    // A non-negative slider index IS an available_steps index (see handleUpdateAnimationStep), so
    // the tick indices need no offset even when min is negative.
    const cadenceTicks = useMemo(() => deriveCadenceTicks(timelineAvailability.available_steps, {
        describeChange: (change) => <span>Time steps change from every {change.previousCadence} to every {change.cadence} {animationTimeUnit} here</span>,
    }), [timelineAvailability.available_steps, animationTimeUnit]);

    const renderStepTooltip = useCallback((index: number) => {
        if (index < 0) {
            // Negative indices are not available_steps entries - they step back into a previous run.
            return <span>{formatDisplayDate(moment.utc(props.currentForecastDate).add(index, 'hours'))}<br />Previous forecast run</span>;
        }
        if (timelineAvailability.labels) return <span>{timelineAvailability.labels[index]}</span>;
        const stepValue = timelineAvailability.available_steps[index];
        if (stepValue === undefined) return null;
        return <span>
            {formatDisplayDate(moment.utc(props.currentForecastDate).add(stepValue, animationTimeUnit))}
            <br />T+{stepValue} {animationTimeUnit}
        </span>;
    }, [timelineAvailability, props.currentForecastDate, animationTimeUnit, formatDisplayDate]);

    const describeStep = useCallback((index: number) => {
        if (index < 0) return `Step ${index}, previous forecast run`;
        const position = `Step ${index + 1} of ${stepIndex.total}`;
        const additional = stepIndex.additionalAt(index);
        if (additional) return `${position}, ${additional.sliderLabel} ${animationTimeUnit} summary`;
        const label = timelineAvailability.labels?.[index];
        if (label) return `${position}, ${label}`;
        const stepValue = timelineAvailability.available_steps[index];
        return stepValue === undefined ? position : `${position}, T+${stepValue} ${animationTimeUnit}`;
    }, [timelineAvailability, stepIndex, animationTimeUnit]);

    return (
        <div
            id='timeline-widget'
            className='d-flex flex-md-row flex-column timeline-width timeline-border-all rounded shadow mx-auto p-2 mb-2'
            style={{
                backgroundColor: "rgba(244, 244, 244)",
                overflow: "hidden",
            }}>
            {state && <>
                <div className='pe-2'>
                    <div className='fs-7 ps-4 mb-2' style={{ borderBottom: "3px solid orange" }}>
                        <div className="d-flex flex-row my-auto align-items-center">
                            <div className='me-3'>Forecast - {groupFullName(state.interval)}</div>
                            <div className='me-3'>
                                <ModalWidget title='Layer availabilities'
                                    toggleButtonText={
                                        <div className='d-flex flex-row gap-2'>
                                            <FontAwesomeIcon className='p-0' icon={faInfoCircle} />

                                            {hasDifferentSteps && <div className='text-danger'>Not all layers have the same steps <FontAwesomeIcon icon={faExclamationTriangle} /></div>}
                                        </div>
                                    }>
                                    <div className='d-flex flex-row gap-2'>
                                        {layerAvailabilities && Object.entries(layerAvailabilities).map(([layerName, availability]) => (
                                            <div key={layerName} className='overflow-y-auto'>
                                                <div className="d-flex flex-column align-items-center my-2">
                                                    <div className='btn btn-outline-dark mb-3'
                                                        onClick={() => onSetTimelineAvailability(availability)}> Use availability range</div>
                                                    <strong>{layerName}:</strong>
                                                </div>
                                                <span>Base forecast: {formatDisplayDate(props.currentForecastDate)}</span>
                                                {availability.labels ?
                                                    <span>
                                                        {availability.labels.map((label, idx) => <div>Step [{idx + 1}]: {label}</div>)}
                                                    </span>
                                                    :
                                                    <span>
                                                        {availability.available_steps?.map((stepValue, idx) =>
                                                            <div>Step [{idx + 1}]: time: {formatDisplayDate(moment.utc(props.currentForecastDate).add(stepValue, animationTimeUnit))}, T+{stepValue} {animationTimeUnit}</div>
                                                        )}
                                                    </span>
                                                }
                                                {availability.additional_steps?.map((additional, idx) =>
                                                    <div key={`${idx}-${additional.sliderLabel}`}>
                                                        Step [{availability.max + 2 + idx}]: {additional.sliderLabel} {animationTimeUnit} summary,
                                                        from {formatDisplayDate(moment.utc(props.currentForecastDate).add(additional.step, animationTimeUnit))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </ModalWidget>
                            </div>
                        </div>
                    </div>
                    <div className='mx-auto'>
                        <ForecastDate {...props}
                            forecastIncrement={1}
                            forecastStep={forecastStepUnit}
                            twentyFourHours={animationTimeUnit === DurationConstructor.hours || animationTimeUnit === DurationConstructor.days} />
                    </div>
                </div>
                {timelineAvailability.available_steps.length === 0 &&
                    <div className='d-flex px-5 align-items-center'>The {groupFullName(state.interval)} layers are not available for this forecast. {timelineAvailability.info}</div>
                    ||
                    <div className='d-flex flex-column flex-fill px-3 fs-7'>
                        <div className='d-flex flex-row gap-3 mb-1 text-center justify-content-center'>
                            <div className='form-label mb-2'>
                                {/* htmlFor cannot target a non-labelable element, and this text is the
                                    slider's *value* - so the accessible name is a static string instead. */}
                                <span id="timeline-slider-label" className='visually-hidden'>Forecast lead time</span>
                                {timelineAvailability.run_date && <span>Forecast: {timelineAvailability.run_date}, Lead time:&nbsp;</span>}
                                {activeAdditional ?
                                    <>{formatDisplayDate(selectedDate)}<span>, {activeAdditional.indexLabel} {animationTimeUnit} summary</span></>
                                    : timelineAvailability.labels && currentStep >= 0 ?
                                        <>{timelineAvailability.labels[currentStep]}</>
                                        :
                                        <>{formatDisplayDate(selectedDate)}{currentStep >= 0 && <span>, T+{timelineAvailability.available_steps[currentStep]} {animationTimeUnit}</span>}</>
                                }
                            </div>
                        </div>
                        {/* align-items-start, not -center: both children carry the track's own top
                            margin, so aligning their tops puts the boxes on the track's centreline
                            regardless of how far the captions hang below them. */}
                        <div className='d-flex flex-row align-items-start gap-3'>
                            <div className='flex-fill'>
                                <TimelineSlider
                                    id="timeline-slider"
                                    ariaLabelledBy="timeline-slider-label"
                                    min={timelineAvailability.min}
                                    max={timelineAvailability.max}
                                    value={currentStep}
                                    step={1}
                                    disabled={isPreloading}
                                    ticks={cadenceTicks}
                                    // The selection is on one of the buttons, which shows it instead.
                                    showThumb={activeAdditionalIndex === null}
                                    renderTooltip={renderStepTooltip}
                                    ariaValueText={describeStep}
                                    onCommit={handleUpdateAnimationStep}
                                />
                            </div>
                            <AdditionalSteps
                                steps={stepIndex.additional}
                                activeIndex={activeAdditionalIndex}
                                disabled={isPreloading}
                                onSelect={(index) => handleUpdateAnimationStep(timelineAvailability.max + 1 + index)} />
                        </div>
                        <div className='d-flex flex-row mx-auto'>
                            <div className={`btn ${isPreloading ? 'disabled' : ''}`} onClick={() => !isPreloading && handleUpdateAnimationStep(Math.max(timelineAvailability.min, currentStep - 1))}><FontAwesomeIcon icon={faBackward} /></div>
                            {activeAdditional ?
                                <div className='mt-2'>Step {activeAdditional.indexLabel}</div>
                                :
                                <div className='mt-2'>Step {currentStep >= 0 ? currentStep + 1 : currentStep}/{stepIndex.total}</div>
                            }
                            <div className={`btn ${isPreloading ? 'disabled' : ''}`} onClick={() => !isPreloading && handleUpdateAnimationStep(Math.min(stepIndex.maxIndex, currentStep + 1))}><FontAwesomeIcon icon={faForward} /></div>
                            <Tooltip title="Preloads the next 10 steps and sets step to 0" placement="top" disableInteractive>
                                <div className={`btn ${isPreloading ? 'disabled' : ''}`} onClick={handlePreload}><FontAwesomeIcon icon={faRotateRight} /></div>
                            </Tooltip>
                        </div>
                    </div>
                }
            </>
                ||
                <div className='p-3 fw-bold fs-5'>Select a time step for timeline navigation</div>}
        </div>);
})